import { useEffect, useRef, useState } from "react";

import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { useParallax, softEaseOut } from "@/hooks/useParallax";
import { useTypewriter } from "@/hooks/useTypewriter";
import { useTheme } from "@/contexts/ThemeContext";
import { useOpenSandbox } from "@/contexts/ModalContext";

// ─── Canvas animation types & helpers ────────────────────────────────────────
type RGB = [number, number, number];

const getColors = (theme: "light" | "dark") => ({
  primary: [91, 75, 245] as RGB,
  accent: [0, 186, 214] as RGB,
  nodeBase: (theme === "dark" ? [226, 232, 240] : [15, 26, 46]) as RGB,
  line: (theme === "dark" ? [148, 163, 184] : [103, 116, 135]) as RGB,
});

const rgba = ([r, g, b]: RGB, a: number) => `rgba(${r},${g},${b},${a})`;

const NODE_DEFS: { rx: number; ry: number }[] = [
  { rx: 0.12, ry: 0.22 },
  { rx: 0.30, ry: 0.12 },
  { rx: 0.55, ry: 0.18 },
  { rx: 0.80, ry: 0.25 },
  { rx: 0.90, ry: 0.55 },
  { rx: 0.68, ry: 0.72 },
  { rx: 0.38, ry: 0.80 },
  { rx: 0.15, ry: 0.68 },
];

interface AnimNode {
  x: number;
  y: number;
  r: number;
  glow: number;
  glowColor: RGB;
}
interface AnimEdge {
  a: number;
  b: number;
}
interface Pulse {
  edgeIdx: number;
  t: number;
  reversed: boolean;
  speed: number;
  color: RGB;
  reached: boolean;
}

const euclidean = (a: AnimNode, b: AnimNode) => Math.hypot(a.x - b.x, a.y - b.y);


// Token types for syntax highlighting
type TokenType = 'comment' | 'string' | 'keyword' | 'property' | 'punctuation' | 'number' | 'function' | 'default';
interface Token {
  text: string;
  type: TokenType;
}

const tokenizeCode = (code: string): Token[] => {
  const tokens: Token[] = [];
  let i = 0;
  while (i < code.length) {
    if (code.slice(i, i + 2) === '//') {
      let end = code.indexOf('\n', i);
      if (end === -1) end = code.length;
      tokens.push({ text: code.slice(i, end), type: 'comment' });
      i = end;
      continue;
    }
    if (code[i] === '"') {
      let end = i + 1;
      while (end < code.length && code[end] !== '"') end++;
      tokens.push({ text: code.slice(i, end + 1), type: 'string' });
      i = end + 1;
      continue;
    }
    if (/\d/.test(code[i]) && (i === 0 || /[\s:,(\[]/.test(code[i - 1]))) {
      let end = i;
      while (end < code.length && /\d/.test(code[end])) end++;
      tokens.push({ text: code.slice(i, end), type: 'number' });
      i = end;
      continue;
    }
    const keywords = ['await', 'const', 'if', 'return', 'method', 'headers', 'body'];
    let foundKeyword = false;
    for (const kw of keywords) {
      if (code.slice(i, i + kw.length) === kw && (i === 0 || /[\s{(,\n]/.test(code[i - 1]))) {
        tokens.push({ text: kw, type: 'keyword' });
        i += kw.length;
        foundKeyword = true;
        break;
      }
    }
    if (foundKeyword) continue;
    const propMatch = code.slice(i).match(/^([a-z_][a-z_0-9]*)(?=\s*:)/i);
    if (propMatch && (i === 0 || /[\s{,]/.test(code[i - 1]))) {
      tokens.push({ text: propMatch[1], type: 'property' });
      i += propMatch[1].length;
      continue;
    }
    const funcMatch = code.slice(i).match(/^(fetch|JSON\.stringify|console\.log)(?=\()/);
    if (funcMatch) {
      tokens.push({ text: funcMatch[1], type: 'function' });
      i += funcMatch[1].length;
      continue;
    }
    if (/[{}():,;\[\]]/.test(code[i])) {
      tokens.push({ text: code[i], type: 'punctuation' });
      i++;
      continue;
    }
    tokens.push({ text: code[i], type: 'default' });
    i++;
  }
  return tokens;
};

const getTokenClass = (type: TokenType): string => {
  switch (type) {
    case 'comment':return 'text-muted-foreground/80';
    case 'string':return 'text-accent/70';
    case 'keyword':return 'text-primary/70';
    case 'property':return 'text-warning/70';
    case 'number':return 'text-accent/70';
    case 'punctuation':return 'text-foreground/50';
    case 'function':return 'text-foreground/70';
    default:return 'text-foreground/70';
  }
};

const statStrip = [
{ value: "20%", label: "average no-show rate in restaurants", source: "CGA / UKHospitality 2024" },
{ value: "€18B", label: "lost annually to no-shows (UK alone)", source: "Zonal / GO Technology 2023" },
{ value: "90%", label: "of your customers say reducing no-shows is their #1 priority", source: "ClearSlot partner survey 2025" },
{ value: "2–4h", label: "typical integration time", source: "" }];


const Hero = () => {
  
  const { setOpen: setSandboxOpen } = useOpenSandbox();
  const sectionRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gridParallax = useParallax(sectionRef, 0.1);
  const orbLeftParallax = useParallax(sectionRef, 0.15);
  const orbRightParallax = useParallax(sectionRef, 0.2);
  const { theme } = useTheme();
  const themeRef = useRef(theme);
  useEffect(() => { themeRef.current = theme; }, [theme]);

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (isMobile) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationId: number;
    let pulseInterval: ReturnType<typeof setInterval>;
    const nodes: AnimNode[] = [];
    const edges: AnimEdge[] = [];
    const pulses: Pulse[] = [];
    let W = 0;
    let H = 0;
    let breathT = 0;

    function buildGraph(w: number, h: number) {
      nodes.length = 0;
      edges.length = 0;
      const COLOR = getColors(themeRef.current);
      NODE_DEFS.forEach((def) => {
        nodes.push({ x: def.rx * w, y: def.ry * h, r: 5, glow: 0, glowColor: COLOR.primary });
      });
      const maxDist = Math.min(w, h) * 0.45;
      for (let i = 0; i < nodes.length; i++) {
        const sorted = nodes
          .map((n, j) => ({ j, d: euclidean(nodes[i], n) }))
          .filter(({ j, d }) => j !== i && d < maxDist)
          .sort((a, b) => a.d - b.d);
        const maxConn = 2 + (i % 2);
        sorted.slice(0, maxConn).forEach(({ j }) => {
          if (!edges.find((e) => (e.a === i && e.b === j) || (e.a === j && e.b === i))) {
            edges.push({ a: i, b: j });
          }
        });
      }
    }

    function resize() {
      const dpr = window.devicePixelRatio || 1;
      W = canvas.offsetWidth;
      H = canvas.offsetHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      buildGraph(W, H);
    }

    function spawnPulse() {
      if (edges.length === 0) return;
      const COLOR = getColors(themeRef.current);
      const edgeIdx = Math.floor(Math.random() * edges.length);
      const reversed = Math.random() < 0.5;
      const color: RGB = Math.random() < 0.7 ? COLOR.accent : COLOR.primary;
      pulses.push({ edgeIdx, t: 0, reversed, speed: 0.0018 + Math.random() * 0.0012, color, reached: false });
    }

    function draw(timestamp: number) {
      const COLOR = getColors(themeRef.current);
      const lineAlpha = themeRef.current === "dark" ? 0.18 : 0.12;
      ctx.clearRect(0, 0, W, H);
      breathT = timestamp * 0.0006;

      edges.forEach(({ a, b }) => {
        ctx.beginPath();
        ctx.moveTo(nodes[a].x, nodes[a].y);
        ctx.lineTo(nodes[b].x, nodes[b].y);
        ctx.strokeStyle = rgba(COLOR.line, lineAlpha);
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      pulses.forEach((p) => {
        const edge = edges[p.edgeIdx];
        const src = p.reversed ? nodes[edge.b] : nodes[edge.a];
        const dst = p.reversed ? nodes[edge.a] : nodes[edge.b];
        p.t += p.speed;
        if (!p.reached && p.t >= 0.95) {
          p.reached = true;
          dst.glow = 1.0;
          dst.glowColor = p.color;
        }
        if (p.t > 1) return;
        const px = src.x + (dst.x - src.x) * p.t;
        const py = src.y + (dst.y - src.y) * p.t;
        const [r, g, b] = p.color;
        const trailStart = Math.max(0, p.t - 0.18);
        const grad = ctx.createLinearGradient(src.x, src.y, dst.x, dst.y);
        grad.addColorStop(trailStart, `rgba(${r},${g},${b},0)`);
        grad.addColorStop(p.t, `rgba(${r},${g},${b},0.5)`);
        ctx.beginPath();
        ctx.moveTo(src.x + (dst.x - src.x) * trailStart, src.y + (dst.y - src.y) * trailStart);
        ctx.lineTo(px, py);
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(px, py, 3, 0, Math.PI * 2);
        ctx.fillStyle = rgba(p.color, 0.9);
        ctx.fill();
        const headGlow = ctx.createRadialGradient(px, py, 0, px, py, 14);
        headGlow.addColorStop(0, rgba(p.color, 0.25));
        headGlow.addColorStop(1, rgba(p.color, 0));
        ctx.beginPath();
        ctx.arc(px, py, 14, 0, Math.PI * 2);
        ctx.fillStyle = headGlow;
        ctx.fill();
      });

      for (let i = pulses.length - 1; i >= 0; i--) {
        if (pulses[i].t > 1) pulses.splice(i, 1);
      }

      nodes.forEach((node, idx) => {
        node.glow *= 0.97;
        const breath = 0.06 + 0.04 * Math.sin(breathT + idx * 1.3);
        const totalGlow = Math.min(1, node.glow + breath);
        const glowR = 12 + totalGlow * 20;
        if (totalGlow > 0.01) {
          const gc = node.glow > 0.05 ? node.glowColor : COLOR.primary;
          const grd = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, glowR);
          grd.addColorStop(0, rgba(gc, totalGlow * 0.35));
          grd.addColorStop(1, rgba(gc, 0));
          ctx.beginPath();
          ctx.arc(node.x, node.y, glowR, 0, Math.PI * 2);
          ctx.fillStyle = grd;
          ctx.fill();
        }
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.r, 0, Math.PI * 2);
        ctx.fillStyle = rgba(COLOR.nodeBase, 0.18 + totalGlow * 0.25);
        ctx.fill();
        const ringColor = node.glow > 0.1 ? node.glowColor : COLOR.line;
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.r, 0, Math.PI * 2);
        ctx.strokeStyle = rgba(ringColor, 0.35 + totalGlow * 0.45);
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      animationId = requestAnimationFrame(draw);
    }

    resize();
    window.addEventListener("resize", resize);
    const ro = new ResizeObserver(() => resize());
    ro.observe(canvas);

    const startLoop = () => {
      spawnPulse();
      setTimeout(spawnPulse, 600);
      setTimeout(spawnPulse, 1200);
      pulseInterval = setInterval(spawnPulse, 1800 + Math.random() * 1200);
      animationId = requestAnimationFrame(draw);
    };

    const stopLoop = () => {
      if (animationId) cancelAnimationFrame(animationId);
      if (pulseInterval) clearInterval(pulseInterval);
    };

    const onVisibility = () => {
      if (document.hidden) {
        stopLoop();
      } else {
        startLoop();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    startLoop();

    return () => {
      stopLoop();
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVisibility);
      ro.disconnect();
    };
  }, []);

  const terminalCode = `// trust-check.js — fail-open pattern
const res = await fetch("https://clearslot-production.up.railway.app/v1/score/+4520123456?platform_id=YOUR_PLATFORM_ID", {
  headers: { "x-api-key": "YOUR_API_KEY" }
});

if (!res.ok) return proceed(); // fail-open

const { score, active_signals } = await res.json();
// score: 82, active_signals: 1

if (score < 40) requireDeposit();
// Otherwise: book normally. No friction.`;

  const { displayedText, cursorVisible } = useTypewriter({
    text: terminalCode,
    typingSpeed: 10,
    pauseDuration: 5000,
    loop: true
  });

  const tokens = tokenizeCode(displayedText);

  return (
    <section ref={sectionRef} className="relative min-h-screen overflow-hidden pt-24 md:pt-16">
      {/* Background effects with parallax */}
      <motion.div style={{ y: gridParallax }} className="absolute inset-0 bg-grid opacity-50" />
      <div className="hero-gradient absolute inset-0" />

      {/* Animated glow orbs (subdued behind network graph) */}
      <motion.div style={{ y: orbLeftParallax }} className="absolute left-1/4 top-1/3 h-96 w-96 rounded-full bg-accent/5 blur-[120px]" animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div style={{ y: orbRightParallax }} className="absolute right-1/4 bottom-1/3 h-80 w-80 rounded-full bg-primary/5 blur-[100px]" animate={{ scale: [1.2, 1, 1.2], opacity: [0.3, 0.5, 0.3] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} />

      {/* Network graph canvas */}
      <canvas ref={canvasRef} aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 h-full w-full" />

      <div className="container relative mx-auto flex min-h-[calc(100vh-4rem)] items-center px-6">
        <div className="flex flex-col w-full items-center gap-6">
          {/* Centered text column */}
          <div className="text-center order-1 mx-auto relative z-10 max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: softEaseOut }} className="mb-4 lg:mb-6">
              <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-4 py-1.5 text-sm font-medium text-accent shadow-[0_0_20px_rgba(var(--accent-rgb),0.3)]">
                <Sparkles className="h-4 w-4" />
                Neutral Booking Coordination Infrastructure
              </span>
            </motion.div>

            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1, ease: softEaseOut }} className="mb-4 lg:mb-6 max-w-5xl text-3xl font-bold leading-[1.25] tracking-tighter text-foreground sm:text-3xl md:text-4xl lg:text-5xl">
              A guest books three restaurants for Saturday night.
              <span className="block text-gradient pr-1 pb-3">Shows up at one. The two others held the table for nothing.</span>
            </motion.h1>

            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2, ease: softEaseOut }} className="mb-6 lg:mb-10 text-base lg:text-lg text-muted-foreground md:text-xl text-center">
              <p>The two restaurants have a problem — and as a platform, so do you.</p>
              <p className="mt-2 text-sm md:text-base">Three endpoints. Live before your next lost cover.</p>
            </motion.div>

            {/* StatStrip — moved above CTAs */}
            <div className="mb-8 lg:mb-10">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-border pt-8">
                {statStrip.map((stat, i) =>
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.5 + i * 0.08 }}
                  className="text-center">

                    <p className="text-2xl font-bold text-foreground md:text-3xl">{stat.value}</p>
                    <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
                    <p className="text-[10px] text-muted-foreground/70 mt-0.5 italic text-center truncate">{stat.source}</p>
                  </motion.div>
                )}
              </div>
            </div>

            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3, ease: softEaseOut }} className="flex flex-col gap-3 sm:flex-row justify-center">
              <Button variant="hero" size="default" className="text-sm w-full sm:w-auto h-11" onClick={() => setSandboxOpen(true)}>
                Try the Sandbox
              </Button>
            </motion.div>

            <p className="mt-4 text-center text-sm text-muted-foreground">
              Not ready to apply?{" "}
              <a href="mailto:hello@clearslot.io" className="hover:underline text-foreground">
                Send us a question →
              </a>
            </p>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5, ease: softEaseOut }}
              className="mt-4 text-center text-xs font-mono text-muted-foreground/70 tracking-wide"
            >
              GDPR-native • EU-hosted
            </motion.p>
          </div>

        </div>
      </div>

    </section>);

};

export default Hero;
